/**
 * Monitoreo en vivo de cobros / transferencias / estado de collectors.
 *
 * Vista viva de las mismas fuentes que alimentan los informes por email
 * (rpsummary / summary ar / accountsummary de yolopago_update):
 *   GET /monitoreo/cobros          — received_payments del día de negocio
 *   GET /monitoreo/transferencias  — ar_operations por fecha de negocio
 *   GET /monitoreo/collectors      — saldos + pendiente de liquidación
 *
 * Los endpoints cachean 10-30s server-side, así que el polling de 15s es barato.
 */
import { authFetch, authHeader, jsonOrThrow, qsFrom } from "./monitoring";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

// ---------- Cobros ----------
export interface CobroCollectorRow {
  collector: string;
  sub_processor: string;
  account: string;
  count: number;
  total: number;
  min: number;
  max: number;
  avg: number;
  first: string;          // ISO local del acquirer (de order_sk)
  last: string;
  charge_rate?: number;   // solo AR#BIND
  net?: number;           // NetRv estimado, solo AR#BIND
}

export interface CobrosAcquirer {
  key: string;            // AR#BIND | AR#CMFP | MX#STP
  country: "AR" | "MX";
  business_date: string;  // yyyyMMdd
  currency: "ARS" | "MXN";
  collectors: CobroCollectorRow[];
  totals: { count: number; total: number };
}

export interface CobroFeedItem {
  ts: string;
  acquirer: string;
  collector: string;
  amount: number;
  currency: string;
  status: string;
  from: string;
  payment_id: string;
  reference: string;
}

export interface Cobros {
  generated_at: string;
  acquirers: CobrosAcquirer[];
  feed: CobroFeedItem[];
}

export async function getCobros(params?: { date?: string; feed_limit?: number }): Promise<Cobros> {
  const url = `${API_BASE}/monitoreo/cobros${qsFrom({
    date: params?.date,
    feed_limit: params?.feed_limit != null ? String(params.feed_limit) : undefined,
  })}`;
  const res = await authFetch(url, { headers: authHeader() });
  return jsonOrThrow(res, "cobros");
}

// ---------- Transferencias ----------
export interface TransferenciaRow {
  vendor: string;
  group: string;
  estado: "Transferido" | "Acreditado" | "A revisar";
  payment_status: string;
  cobros: number | null;
  es_fondeo: boolean;
  currency: string;       // ars | mxn
  net: number;
  total: number;
  fee: number;
  invoice: string;
  merchant: string;
  cuenta_totales: boolean;
}

export interface TransferenciasTotales {
  comision: number;
  transferido: number;
  acreditado: number;
  a_revisar: number;
}

export interface Transferencias {
  generated_at: string;
  date: string;                             // yyyyMMdd consultada
  business_dates: { current: string; previous: string };
  rows: TransferenciaRow[];
  totales: Record<string, TransferenciasTotales>;  // por moneda (ars/mxn)
}

export async function getTransferencias(params?: { date?: string }): Promise<Transferencias> {
  const url = `${API_BASE}/monitoreo/transferencias${qsFrom({ date: params?.date })}`;
  const res = await authFetch(url, { headers: authHeader() });
  return jsonOrThrow(res, "transferencias");
}

// ---------- Collectors ----------
export interface CollectorRow {
  collector: string;
  processor: string;
  sub_processor: string;
  account: string;
  currency: "ARS" | "MXN";
  disponible: number;
  pendiente: number;
  bruto: number;
  charge_rate: number;
  neto: number;
}

export interface CollectorsTotales {
  disponible: number;
  pendiente: number;
  bruto: number;
  neto: number;
}

export interface Collectors {
  generated_at: string;
  business_dates: Record<string, string>;   // {AR: yyyyMMdd, MX: yyyyMMdd}
  rows: CollectorRow[];
  totales: Record<string, CollectorsTotales>;  // por moneda (ARS/MXN)
  errors: { collector: string; error: string }[];
}

export async function getCollectors(): Promise<Collectors> {
  const res = await authFetch(`${API_BASE}/monitoreo/collectors`, { headers: authHeader() });
  return jsonOrThrow(res, "collectors");
}

// ---------- Buscador de alias / CVU (CP-154) ----------
// ¿A qué collector corresponde la cuenta a la que el pagador transfirió?
// Fuente: payment_accounts (core, read-only). Va como query param de
// /monitoreo/collectors porque la resource policy de BillingFn no admite
// una ruta HttpApi más (ver template.yaml).
export interface CuentaImputada {
  collector:      string | null;
  alias:          string | null;
  cvu:            string;
  pais:           string | null;   // AR | MX
  banco:          string | null;   // BIND | CMFP | CMF | STP
  processor:      string | null;   // ej. coelsa
  customer_id:    string | null;
  vendor_id:      number | null;
  related_tax_id: string | null;
  email:          string | null;
  asignada:       boolean;
  historia:       string | null;   // log de vida de la cuenta (creación, alias, updates)
  // Enriquecimiento desde collection_config:
  collector_nombre?:   string | null;
  collector_tipo?:     string | null;   // persona humana | persona jurídica
  collector_activo?:   boolean;
  acquirer?:           string | null;
  payments_processor?: string | null;   // ej. AR#BIND#...
}

export interface BuscarCuentaResult {
  query: string;
  tipo:  "cvu" | "alias" | null;
  items: CuentaImputada[];
}

export async function buscarCuenta(q: string): Promise<BuscarCuentaResult> {
  // OJO: qsFrom ya incluye el "?" inicial.
  const res = await authFetch(
    `${API_BASE}/monitoreo/collectors${qsFrom({ buscar: q })}`,
    { headers: authHeader() },
  );
  const data: BuscarCuentaResult = await jsonOrThrow(res, "buscarCuenta");
  // Si el backend no reconoció `buscar` devuelve el payload de collectors
  // (sin `items`) — mejor un error claro que una pantalla rota.
  if (!Array.isArray((data as any)?.items)) {
    throw new Error("respuesta inesperada del buscador (sin items)");
  }
  return data;
}

// ---------- Búsqueda por CUIT (CP-522) ----------
// Parte 1: ¿el CUIT/RFC es de un collector? Misma ruta con ?cuit= (la resource
// policy de BillingFn no admite más rutas HttpApi).

export interface CollectorPorCuit {
  collector:               string;
  collector_nombre:        string | null;
  razon_social:            string | null;
  collector_tipo:          string | null;
  collector_activo:        boolean;
  tax_id:                  string | null;
  acquirer:                string | null;
  payments_processor:      string | null;
  payments_sub_processor:  string | null;
}

export interface PadronArca {
  razon_social?:       string;
  condicion_iva_desc?: string;
  direccion?:          string;
  localidad?:          string;
  cod_postal?:         string;
  provincia?:          string;
  consultado_at?:      string;
}

export interface BuscarCuitResult {
  query:   string;
  tipo:    "cuit" | "rfc" | null;
  cuit:    string | null;     // normalizado (11 dígitos o RFC en mayúsculas)
  valido:  boolean;
  error?:  string;
  items:   CollectorPorCuit[];
  padron?: PadronArca | null;
}

export async function buscarCuit(q: string): Promise<BuscarCuitResult> {
  const res = await authFetch(
    `${API_BASE}/monitoreo/collectors${qsFrom({ cuit: q })}`,
    { headers: authHeader() },
  );
  const data: BuscarCuitResult = await jsonOrThrow(res, "buscarCuit");
  if (!Array.isArray((data as any)?.items)) {
    throw new Error("respuesta inesperada del buscador por CUIT (sin items)");
  }
  return data;
}

// ---------- Auditoría de llamados de una cuenta ----------
// `description` de payment_accounts ("Last Update:<ISO>") dice cuándo se tocó la
// cuenta pero no quién: eso solo está en los logs de la API externa. La consulta
// es async (Logs Insights escanea GB) — arranca con una llamada y se reconsulta
// con el query_id hasta que termina. Nunca bloquea 29s de API Gateway.

export type AuditTipo =
  | "alta" | "alias" | "cierre" | "apertura" | "cambio"
  | "pago" | "cobro" | "coelsa" | "llamado" | "respuesta";

export interface AuditEvento {
  t:        string;        // ISO UTC
  tipo:     AuditTipo;
  titulo:   string;
  metodo?:  string;
  path?:    string;
  body?:    string;
  auth?:    string;        // apikey | jwt | sin token
  collector?: string;      // collector dueño de la apikey
  usuario?: string;        // Assumed User / unique_name del JWT
  ip?:      string;
  user_agent?: string;
  raw?:     string;
}

export interface AuditoriaCuenta {
  cvu:       string;
  estado:    "corriendo" | "listo" | "error";
  query_id:  string;
  dias?:     number;
  desde?:    string;
  hasta?:    string;
  eventos?:  AuditEvento[];
  truncado?: boolean;
  lineas_descartadas?: number;
  error?:    string;
}

async function _auditoria(params: Record<string, string | undefined>): Promise<AuditoriaCuenta> {
  const res = await authFetch(`${API_BASE}/monitoreo/collectors${qsFrom(params)}`, { headers: authHeader() });
  return jsonOrThrow(res, "auditoriaCuenta");
}

/** Arranca la búsqueda. `ref` ancla la ventana (normalmente el Last Update de la cuenta). */
export function iniciarAuditoria(cvu: string, dias: number, ref?: string | null): Promise<AuditoriaCuenta> {
  return _auditoria({ auditoria: cvu, dias: String(dias), ref: ref || undefined });
}

/** Reconsulta hasta que Insights termina. `onTick` avisa cada reintento. */
export async function esperarAuditoria(
  cvu: string, queryId: string,
  opts: { intentos?: number; intervaloMs?: number; onTick?: (n: number) => void } = {},
): Promise<AuditoriaCuenta> {
  const intentos = opts.intentos ?? 90;          // ~3 min con intervalo de 2s
  const intervalo = opts.intervaloMs ?? 2000;
  for (let i = 0; i < intentos; i++) {
    await new Promise((r) => setTimeout(r, intervalo));
    opts.onTick?.(i + 1);
    const r = await _auditoria({ auditoria: cvu, qid: queryId });
    if (r.estado !== "corriendo") return r;
  }
  throw new Error("la búsqueda en los logs tardó demasiado; probá con menos días");
}

// ---------- Serie mensual por collector ----------
// Sale del rollup (webadmin2-cobros-rollup), no de received_payments en vivo.
// El mes es por FECHA DE NEGOCIO: un cobro del viernes 31 a las 15:00 AR cae en el
// mes siguiente (`month_basis`).

/** Celda de un mes. `null` (no este tipo) = mes fuera de la cobertura del rollup:
 *  no sabemos, y NO es lo mismo que cero cobros. */
export interface SerieMesCell {
  count:  number;
  amount: number;
  avg:    number;
  days:   number;   // días de negocio con al menos un cobro
}

export interface SerieCollectorRow {
  collector:     string;
  sub_processor: string;
  account:       string;
  /** yyyyMM → celda, o null si el mes está fuera de cobertura. */
  months: Record<string, SerieMesCell | null>;
  total:  { count: number; amount: number; avg: number };
}

export interface SerieAcquirer {
  key:         string;            // AR#BIND | AR#CMFP | MX#STP
  country:     "AR" | "MX";
  currency:    "ARS" | "MXN";
  cover_start: string;            // yyyyMM más viejo con cobertura
  collectors:  SerieCollectorRow[];
  totals_by_month: Record<string, { count: number; amount: number; collectors: number }>;
  total: { count: number; amount: number };
}

export interface SerieMensual {
  generated_at: string;
  source:       string;
  month_basis:  string;           // "business_date"
  months:       string[];         // yyyyMM, del más viejo al más nuevo
  coverage: {
    first_date:      string;      // yyyyMMdd
    last_date:       string;
    partial_month:   string;      // yyyyMM del mes en curso, si aplica
    through_date:    string;      // último día agregado del mes parcial
    retention_floor: Record<string, string>;   // acquirer → yyyyMMdd
  };
  warnings:  string[];
  acquirers: SerieAcquirer[];
  /** A propósito NO hay total global de monto: sumar ARS + MXN no significa nada. */
  totals_by_currency: Record<string, { count: number; amount: number }>;
}

export type SerieSource = "liquidado" | "cobrado";

export async function getSerieMensual(params?: {
  months?: number; from?: string; to?: string;
  acquirer?: string; collector?: string; includeRejected?: boolean; source?: SerieSource;
}): Promise<SerieMensual> {
  const url = `${API_BASE}/monitoreo/serie-mensual${qsFrom({
    months:    params?.months != null ? String(params.months) : undefined,
    from:      params?.from,
    to:        params?.to,
    acquirer:  params?.acquirer,
    collector: params?.collector,
    include_rejected: params?.includeRejected === false ? "0" : undefined,
    source:    params?.source,
  })}`;
  const res = await authFetch(url, { headers: authHeader() });
  return jsonOrThrow(res, "serie-mensual");
}

export interface SerieDiaRow {
  date:   string;   // yyyyMMdd
  count:  number;
  amount: number;
  avg:    number;
  min:    number;
  max:    number;
}

export interface SerieDias {
  generated_at: string;
  acquirer:  string;
  collector: string;
  month:     string;
  currency:  string;
  days:      SerieDiaRow[];
  total:     { count: number; amount: number };
}

/** Drill-down diario. Va en el mismo endpoint con `detail=dias` — una ruta HttpApi
 *  propia no entra en la resource policy de la lambda (tope 20480 bytes). */
export async function getSerieMensualDias(params: {
  acquirer: string; collector: string; month: string; includeRejected?: boolean; source?: SerieSource;
}): Promise<SerieDias> {
  const url = `${API_BASE}/monitoreo/serie-mensual${qsFrom({
    detail:    "dias",
    acquirer:  params.acquirer,
    collector: params.collector,
    month:     params.month,
    include_rejected: params.includeRejected === false ? "0" : undefined,
    source:    params.source,
  })}`;
  const res = await authFetch(url, { headers: authHeader() });
  return jsonOrThrow(res, "serie-mensual/dias");
}

export interface LiqDiariaRow {
  date:     string;   // yyyyMMdd
  acquirer: string;
  account:  string;
  country:  string;
  currency: string;
  count:    number;
  amount:   number;
}

export interface LiquidacionesDiarias {
  source:       "liquidado";
  generated_at: string;
  months:       string[];
  coverage:     { first_date: string; last_date: string; partial_month: string; through_date: string };
  rows:         LiqDiariaRow[];   // total por (día, acquirer, cuenta), TODOS los collectors
}

/** Liquidado por DÍA sumando TODOS los collectors, por (día, acquirer, cuenta).
 *  Va en el mismo endpoint con `detail=diario` (la resource policy de la lambda no
 *  admite una ruta propia). El filtrado por cuenta (CMF compartida/propias) lo
 *  aplica el front sobre estas filas. Alimenta la descarga del reporte diario. */
export async function getLiquidacionesDiarias(params?: {
  months?: number; from?: string; to?: string; acquirer?: string;
}): Promise<LiquidacionesDiarias> {
  const url = `${API_BASE}/monitoreo/serie-mensual${qsFrom({
    detail:   "diario",
    months:   params?.months != null ? String(params.months) : undefined,
    from:     params?.from,
    to:       params?.to,
    acquirer: params?.acquirer,
  })}`;
  const res = await authFetch(url, { headers: authHeader() });
  return jsonOrThrow(res, "serie-mensual/diario");
}

/** yyyyMM → "ago 2026" (etiqueta de columna). */
export function fmtMonth(yyyymm: string): string {
  if (!/^\d{6}$/.test(yyyymm)) return yyyymm;
  const d = new Date(Number(yyyymm.slice(0, 4)), Number(yyyymm.slice(4, 6)) - 1, 1);
  return d.toLocaleDateString("es-AR", { month: "short", year: "numeric" });
}

// ---------- Cuenta CMF compartida ----------
/** La mayoría de los collectors AR#CMFP cobran en esta cuenta
 *  (`payments_processor_customer_account` en collection_config); el resto tiene
 *  cuenta propia. Los selectores de cuenta de las páginas de monitoreo separan
 *  ambos universos. */
export const CUENTA_CMF_COMPARTIDA = "000##0552##01##30054402";
/** Solapa donde se ofrece el selector de cuenta. Se gatea por acquirer y no por
 *  "hay filas con esa cuenta": el valor de collection_config puede aparecer en
 *  collectors de otros procesadores y el menú no tiene sentido ahí. */
export const ACQ_CMF = "AR#CMFP";
export const CUENTA_CMF_CORTA = CUENTA_CMF_COMPARTIDA.split("##").pop()!;

export const CUENTAS = [
  { value: "todas",  label: "Todas las cuentas" },
  { value: "comp",   label: `Cuenta ${CUENTA_CMF_CORTA} (compartida)` },
  { value: "otras",  label: "Otras cuentas (propias)" },
] as const;

export type Cuenta = (typeof CUENTAS)[number]["value"];

export const matchCuenta = (c: Cuenta, account: string) =>
  c === "todas" ? true
  : c === "comp" ? account === CUENTA_CMF_COMPARTIDA
  : account !== CUENTA_CMF_COMPARTIDA;

/** Sufijo para etiquetas "TOTAL …" y leyendas. */
export const cuentaSufijo = (c: Cuenta) =>
  c === "comp" ? ` · cta ${CUENTA_CMF_CORTA}` : c === "otras" ? " · otras cuentas" : "";

// ---------- Formato ----------
const moneyFmt: Record<string, Intl.NumberFormat> = {};

export function fmtMoney(n: number, currency: string): string {
  const cur = (currency || "ARS").toUpperCase() === "MXN" ? "MXN" : "ARS";
  moneyFmt[cur] ??= new Intl.NumberFormat("es-AR", {
    style: "currency", currency: cur, maximumFractionDigits: 2, minimumFractionDigits: 2,
  });
  return moneyFmt[cur].format(n);
}

/** yyyyMMdd → dd/MM/yyyy (fecha de negocio, sin timezone). */
export function fmtBd(bd: string): string {
  if (!/^\d{8}$/.test(bd)) return bd;
  return `${bd.slice(6, 8)}/${bd.slice(4, 6)}/${bd.slice(0, 4)}`;
}

/** Los timestamps de order_sk vienen en UTC (con o sin "Z"). */
function parseOpTs(ts: string): Date | null {
  if (!ts) return null;
  const iso = /[zZ]|[+-]\d{2}:\d{2}$/.test(ts) ? ts : `${ts}Z`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

const AR_TZ = "America/Argentina/Buenos_Aires";
const opTimeFmt = new Intl.DateTimeFormat("es-AR", {
  timeZone: AR_TZ, hour: "2-digit", minute: "2-digit", second: "2-digit",
});
const opDateTimeFmt = new Intl.DateTimeFormat("es-AR", {
  timeZone: AR_TZ, day: "2-digit", month: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
});

/** Timestamp de order_sk (UTC) → "HH:mm:ss" en hora Argentina. */
export function fmtOpTime(ts: string): string {
  const d = parseOpTs(ts);
  return d ? opTimeFmt.format(d) : ts;
}

/** Timestamp de order_sk (UTC) → "dd/MM HH:mm:ss" en hora Argentina. */
export function fmtOpDateTime(ts: string): string {
  const d = parseOpTs(ts);
  return d ? opDateTimeFmt.format(d) : ts;
}

/** ¿El timestamp (UTC, de order_sk) es de los últimos `min` minutos? */
export function isRecent(ts: string, min: number): boolean {
  const d = parseOpTs(ts);
  return d != null && Date.now() - d.getTime() < min * 60_000;
}
